import { isSupportedSchemaVersion } from '../poolEvents';
import { ClipPlayer } from './clipPlayer';
import logger from '../../logger';

/**
 * HITL-1786 (HITL 8.0.0): the guest's audio for a hear-and-confirm card,
 * pushed by live-transcriber (contract D11, §4.8).
 *
 * task-router asks live-transcriber for the card's turns as it assigns the
 * card; live-transcriber posts one `assist_audio_clip` per assigned agent to
 * that agent's `TASK_CHANNEL_<user id>`, the socket the pool already holds.
 * The console asks for nothing and answers nothing: a clip arrives (about
 * 0.75 s after the card on staging) or it does not, and the card is
 * answerable from the text either way.
 *
 * Audio never goes into Redux: a 3 s clip is ~128 KB of base64. The live
 * card's clip is held here, in one slot, and dropped with the card.
 *
 * The card comes from task-router and the clip from live-transcriber, two hops
 * with no ordering between them, so a clip can land before its card. That clip
 * waits in a second slot for `ASSIST_EARLY_CLIP_HOLD_MS` and is attached when
 * its card shows (`adoptEarly`); past that it is stale and dropped.
 *
 * Next to the clip, the same slot keeps the card's playback telemetry (when
 * the clip came, how often it played, whether it started on its own), read
 * once by the answer (`assist_answer.data`, contract §5.5).
 */

export const ASSIST_AUDIO_CLIP_EVENT = 'assist_audio_clip';
/** live-transcriber's AssemblyAI audio: 16 kHz mono s16le. */
export const CLIP_SAMPLE_RATE = 16000;
export const CLIP_ENCODING = 'pcm_s16le';
/** How long a clip that arrived before its card waits for it. */
export const ASSIST_EARLY_CLIP_HOLD_MS = 5000;

export interface IAssistClip {
  requestId: string;
  samples: Int16Array;
  sampleRate: number;
  durationMs: number;
}

export type AssistClipIgnoreReason =
  | 'unsupported_schema'
  | 'malformed'
  | 'unsupported_encoding'
  | 'empty';

export const base64ToInt16 = (payload: string): Int16Array => {
  if (!payload) return new Int16Array(0);
  const decoded = atob(payload);
  const usable = decoded.length - (decoded.length % 2);
  const bytes = new Uint8Array(usable);
  for (let i = 0; i < usable; i += 1) {
    bytes[i] = decoded.charCodeAt(i);
  }
  return new Int16Array(bytes.buffer);
};

// The clip's wire format is defined in docs/assist-contract.md (§5.4).
/** Pure: one parsed `assist_audio_clip` message to a playable clip. */
export const parseAssistClip = (
  message: any
): { clip: IAssistClip } | { ignored: AssistClipIgnoreReason } => {
  const data = message?.data;
  if (!data || !isSupportedSchemaVersion(data.schema_version)) {
    return { ignored: 'unsupported_schema' };
  }
  if (
    typeof data.request_id !== 'string' ||
    !data.request_id ||
    typeof data.audio_b64 !== 'string'
  ) {
    return { ignored: 'malformed' };
  }
  if (data.encoding !== undefined && data.encoding !== CLIP_ENCODING) {
    return { ignored: 'unsupported_encoding' };
  }
  const sampleRate =
    Number.isInteger(data.sample_rate) && data.sample_rate > 0
      ? data.sample_rate
      : CLIP_SAMPLE_RATE;
  let samples: Int16Array;
  try {
    samples = base64ToInt16(data.audio_b64);
  } catch {
    return { ignored: 'malformed' };
  }
  if (!samples.length) return { ignored: 'empty' };
  return {
    clip: {
      requestId: data.request_id,
      samples,
      sampleRate,
      durationMs: Math.round((samples.length / sampleRate) * 1000),
    },
  };
};

/** Absolute local epoch ms; the answer makes them relative to the card. */
export interface IAssistClipStats {
  clipReceivedAt: number | null;
  plays: number;
  firstPlayAt: number | null;
  autoplayed: boolean;
}

const emptyStats = (): IAssistClipStats => ({
  clipReceivedAt: null,
  plays: 0,
  firstPlayAt: null,
  autoplayed: false,
});

let held: IAssistClip | null = null;
/**
 * Clips that came before their card, oldest first, one per request. A few, not
 * one: a clip for a card this console will drop (one card at a time) must not
 * push out the clip of the card that shows (review B1.5). Each is ≤ 1 MB.
 */
const ASSIST_EARLY_CLIPS_MAX = 3;
let early: { clip: IAssistClip; at: number }[] = [];
let stats: { requestId: string; value: IAssistClipStats } | null = null;
/** Cards whose clip was already started on its own: autoplay happens once. */
let autoplayRequestedFor: string | null = null;

const statsFor = (requestId: string): IAssistClipStats => {
  if (stats?.requestId !== requestId) {
    stats = { requestId, value: emptyStats() };
  }
  return stats.value;
};
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

/** The live card's clip: one slot, since there is one card at a time. */
export const assistClipStore = {
  get: (requestId: string): IAssistClip | null =>
    held?.requestId === requestId ? held : null,
  put(clip: IAssistClip, at: number = Date.now()) {
    held = clip;
    statsFor(clip.requestId).clipReceivedAt = at;
    notify();
  },
  drop(requestId: string) {
    early = early.filter((waiting) => waiting.clip.requestId !== requestId);
    if (stats?.requestId === requestId) stats = null;
    if (autoplayRequestedFor === requestId) autoplayRequestedFor = null;
    if (held?.requestId !== requestId) return;
    held = null;
    notify();
  },
  /**
   * A clip whose card has not shown yet waits for it, beside other requests'
   * (stale ones dropped, the oldest past ASSIST_EARLY_CLIPS_MAX).
   */
  holdEarly(clip: IAssistClip, at: number) {
    early = early.filter(
      (waiting) =>
        waiting.clip.requestId !== clip.requestId &&
        at - waiting.at <= ASSIST_EARLY_CLIP_HOLD_MS
    );
    early.push({ clip, at });
    if (early.length > ASSIST_EARLY_CLIPS_MAX) {
      early.splice(0, early.length - ASSIST_EARLY_CLIPS_MAX);
    }
  },
  /**
   * Called as a card shows: attaches the clip that arrived before it, if it is
   * this card's and still fresh. Returns the clip and how long it waited.
   */
  adoptEarly(
    requestId: string,
    now: number
  ): { clip: IAssistClip; waitedMs: number } | null {
    const waiting = early.find((item) => item.clip.requestId === requestId);
    if (!waiting) return null;
    early = early.filter((item) => item !== waiting);
    const waitedMs = now - waiting.at;
    if (waitedMs > ASSIST_EARLY_CLIP_HOLD_MS) return null;
    held = waiting.clip;
    statsFor(requestId).clipReceivedAt = waiting.at;
    notify();
    return { clip: waiting.clip, waitedMs };
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** The card's playback telemetry so far (zeros when nothing happened). */
  stats(requestId: string): IAssistClipStats {
    return stats?.requestId === requestId ? { ...stats.value } : emptyStats();
  },
  /** Records a playback start. */
  notePlay(requestId: string, at: number, auto: boolean) {
    const value = statsFor(requestId);
    value.plays += 1;
    if (value.firstPlayAt === null) value.firstPlayAt = at;
    if (auto) value.autoplayed = true;
  },
  /**
   * True the first time it is asked for a card: the clip starts on its own
   * once per card, whichever path (late clip, early clip, cue end) gets there
   * first.
   */
  claimAutoplay(requestId: string): boolean {
    if (autoplayRequestedFor === requestId) return false;
    autoplayRequestedFor = requestId;
    return true;
  },
  /** Tests only. */
  reset() {
    held = null;
    early = [];
    stats = null;
    autoplayRequestedFor = null;
    listeners.clear();
  },
};

let sharedPlayer: ClipPlayer | null = null;

/** One player for the lobby: one clip at a time. */
export const getAssistClipPlayer = (): ClipPlayer => {
  if (!sharedPlayer) sharedPlayer = new ClipPlayer();
  return sharedPlayer;
};

/** Tests only. */
export const setAssistClipPlayer = (player: ClipPlayer | null) => {
  sharedPlayer = player;
};

/**
 * Plays the card's clip on the lobby's player at `rate` (pitch preserved) and
 * records the start. `auto`: started on its own, not by the Play button.
 * Resolves whether it started; a refused autoplay is logged
 * (`autoplay_blocked`) and the Play button stays for the agent.
 */
export const playAssistClip = async (
  clip: IAssistClip,
  rate: number,
  {
    auto = false,
    logFields = { requestId: clip.requestId, isTR: true },
  }: { auto?: boolean; logFields?: Record<string, unknown> } = {}
): Promise<boolean> => {
  const started = await getAssistClipPlayer().play(
    clip.samples,
    clip.sampleRate,
    rate
  );
  if (started) {
    assistClipStore.notePlay(clip.requestId, started.startedAt, auto);
    logger.info({
      message: 'Assist clip played',
      ...logFields,
      auto,
      rate,
      durationMs: clip.durationMs,
    });
    return true;
  }
  if (auto) {
    logger.info({
      message: 'Assist clip autoplay_blocked',
      ...logFields,
    });
  }
  return false;
};
